/**
 * Project Library — Sprint 16.
 *
 * Enhanced analysis list with:
 *   - Favorites (star toggle, "Show favorites only" filter)
 *   - Tags (chips, inline add/remove, org-wide tag autocomplete)
 *   - Improved filters: status, favorites, date range, sort
 *   - Excel download link per row (feature-flag gated)
 *   - Industry column derived from analysis inputs
 */

import { useSessionTokenGetter } from '@develio/auth/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { analytics } from '../lib/posthog';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

type ScoreData = {
  grade: string;
  score: number;
  gradeColor: string;
};

type Analysis = {
  id: string;
  projectName: string;
  status: string;
  createdAt: string;
  inputs?: {
    step1?: { naicsCode?: string; industryDescription?: string };
  } | null;
  results?: unknown;
  scoreData?: ScoreData | null;
};

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  intake_received: 'Received',
  calculating: 'Calculating…',
  in_review: 'Complete',
  approved: 'Approved',
  presented: 'Presented',
  archived: 'Archived',
};

const STATUS_COLOR: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600',
  intake_received: 'bg-blue-50 text-blue-700',
  calculating: 'bg-yellow-50 text-yellow-700',
  in_review: 'bg-green-50 text-green-700',
  approved: 'bg-green-100 text-green-800',
  presented: 'bg-purple-50 text-purple-700',
  archived: 'bg-slate-50 text-slate-400',
};

type SortOption = 'newest' | 'oldest' | 'az' | 'za';

/** Star icon — filled or outline */
function StarIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={filled ? 0 : 1.5}
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
      />
    </svg>
  );
}

/** Derive a short industry label from NAICS code */
function industryFromNaics(naics?: string): string {
  if (!naics) return '—';
  const prefix = naics.slice(0, 2);
  const map: Record<string, string> = {
    '11': 'Agriculture',
    '21': 'Mining',
    '22': 'Utilities',
    '23': 'Construction',
    '31': 'Manufacturing',
    '32': 'Manufacturing',
    '33': 'Manufacturing',
    '42': 'Wholesale',
    '44': 'Retail',
    '45': 'Retail',
    '48': 'Transportation',
    '49': 'Warehousing',
    '51': 'Information',
    '52': 'Finance',
    '53': 'Real Estate',
    '54': 'Professional Svcs',
    '55': 'Management',
    '56': 'Admin / Support',
    '61': 'Education',
    '62': 'Healthcare',
    '71': 'Arts / Rec',
    '72': 'Hospitality',
    '81': 'Other Services',
    '92': 'Government',
  };
  return map[prefix] ?? naics;
}

export function AnalysesListRoute() {
  const getToken = useSessionTokenGetter();
  const [analyses, setAnalyses] = useState<Analysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter / sort state
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [favOnly, setFavOnly] = useState(false);
  const [sort, setSort] = useState<SortOption>('newest');
  const [dateAfter, setDateAfter] = useState('');
  const [dateBefore, setDateBefore] = useState('');

  // Per-analysis tags + favorites (keyed by analysis id)
  const [tagsMap, setTagsMap] = useState<Record<string, string[]>>({});
  const [favSet, setFavSet] = useState<Set<string>>(new Set());

  // Tag input state
  const [addingTagFor, setAddingTagFor] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState('');
  const [allOrgTags, setAllOrgTags] = useState<string[]>([]);
  const tagInputRef = useRef<HTMLInputElement>(null);

  const excelEnabled = analytics.isFeatureEnabled('excel-export-enabled');

  // ── Data fetching ──────────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const [analysesRes, orgTagsRes] = await Promise.all([
          fetch(`${API}/api/v1/analyses`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API}/api/v1/analyses/tags/all`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        if (!analysesRes.ok) throw new Error(`${analysesRes.status} ${analysesRes.statusText}`);
        const json = await analysesRes.json();
        const list = json.analyses as Analysis[];
        if (!cancelled) {
          setAnalyses(list);
          if (orgTagsRes.ok) {
            const tagsJson = await orgTagsRes.json();
            setAllOrgTags((tagsJson.tags as string[]) ?? []);
          }
        }

        // Fetch tags + favorites for each analysis in parallel
        if (!cancelled) {
          const tagResults = await Promise.all(
            list.map((a) =>
              fetch(`${API}/api/v1/analyses/${a.id}/tags`, {
                headers: { Authorization: `Bearer ${token}` },
              }).then((r) => r.json() as Promise<{ tags: string[] }>),
            ),
          );
          const favResults = await Promise.all(
            list.map((a) =>
              fetch(`${API}/api/v1/analyses/${a.id}/favorite`, {
                headers: { Authorization: `Bearer ${token}` },
              }).then((r) => r.json() as Promise<{ favorited: boolean }>),
            ),
          );
          if (!cancelled) {
            const newTagsMap: Record<string, string[]> = {};
            const newFavSet = new Set<string>();
            for (let i = 0; i < list.length; i++) {
              const item = list[i];
              const tagResult = tagResults[i];
              const favResult = favResults[i];
              if (item && tagResult) newTagsMap[item.id] = tagResult.tags ?? [];
              if (item && favResult?.favorited) newFavSet.add(item.id);
            }
            setTagsMap(newTagsMap);
            setFavSet(newFavSet);
          }
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  // ── Favorites toggle ───────────────────────────────────────────────────────

  const toggleFavorite = useCallback(
    async (analysisId: string) => {
      const isFav = favSet.has(analysisId);
      // Optimistic update
      setFavSet((prev) => {
        const next = new Set(prev);
        if (isFav) next.delete(analysisId);
        else next.add(analysisId);
        return next;
      });
      try {
        const token = await getToken();
        await fetch(`${API}/api/v1/analyses/${analysisId}/favorite`, {
          method: isFav ? 'DELETE' : 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        // Revert on error
        setFavSet((prev) => {
          const next = new Set(prev);
          if (isFav) next.add(analysisId);
          else next.delete(analysisId);
          return next;
        });
      }
    },
    [favSet, getToken],
  );

  // ── Tags ──────────────────────────────────────────────────────────────────

  const addTag = useCallback(
    async (analysisId: string, tag: string) => {
      const normalized = tag.trim().toLowerCase();
      if (!normalized) return;
      setTagsMap((prev) => ({
        ...prev,
        [analysisId]: [...new Set([...(prev[analysisId] ?? []), normalized])],
      }));
      setTagInput('');
      setAddingTagFor(null);
      try {
        const token = await getToken();
        await fetch(`${API}/api/v1/analyses/${analysisId}/tags`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ tag: normalized }),
        });
        if (!allOrgTags.includes(normalized)) {
          setAllOrgTags((prev) => [...prev, normalized].sort());
        }
      } catch {
        // Revert
        setTagsMap((prev) => ({
          ...prev,
          [analysisId]: (prev[analysisId] ?? []).filter((t) => t !== normalized),
        }));
      }
    },
    [getToken, allOrgTags],
  );

  const removeTag = useCallback(
    async (analysisId: string, tag: string) => {
      setTagsMap((prev) => ({
        ...prev,
        [analysisId]: (prev[analysisId] ?? []).filter((t) => t !== tag),
      }));
      try {
        const token = await getToken();
        await fetch(`${API}/api/v1/analyses/${analysisId}/tags/${encodeURIComponent(tag)}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        // Revert
        setTagsMap((prev) => ({
          ...prev,
          [analysisId]: [...(prev[analysisId] ?? []), tag],
        }));
      }
    },
    [getToken],
  );

  // ── Download Excel ────────────────────────────────────────────────────────

  const downloadXlsx = useCallback(
    async (a: Analysis) => {
      try {
        const token = await getToken();
        const res = await fetch(`${API}/api/v1/analyses/${a.id}/xlsx`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const el = document.createElement('a');
        el.href = url;
        el.download = `${a.projectName.replace(/[^a-z0-9]/gi, '_')}_impact_report.xlsx`;
        el.click();
        URL.revokeObjectURL(url);
      } catch {
        /* non-fatal */
      }
    },
    [getToken],
  );

  // ── Delete ────────────────────────────────────────────────────────────────

  const deleteAnalysis = useCallback(
    async (a: Analysis) => {
      const ok = window.confirm(
        `Delete "${a.projectName}"? This permanently removes the analysis and its results.`,
      );
      if (!ok) return;
      const prev = analyses;
      setAnalyses((list) => list.filter((x) => x.id !== a.id));
      try {
        const token = await getToken();
        const res = await fetch(`${API}/api/v1/analyses/${a.id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok && res.status !== 204) {
          throw new Error(`${res.status}`);
        }
      } catch (e) {
        setAnalyses(prev);
        setError(e instanceof Error ? `Delete failed: ${e.message}` : 'Delete failed');
      }
    },
    [analyses, getToken],
  );

  // ── Filter + sort ─────────────────────────────────────────────────────────

  const filtered = analyses
    .filter((a) => {
      if (!a.projectName.toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter !== 'all' && a.status !== statusFilter) return false;
      if (favOnly && !favSet.has(a.id)) return false;
      if (dateAfter && new Date(a.createdAt) < new Date(dateAfter)) return false;
      if (dateBefore && new Date(a.createdAt) > new Date(dateBefore)) return false;
      return true;
    })
    .sort((a, b) => {
      if (sort === 'newest')
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      if (sort === 'oldest')
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      if (sort === 'az') return a.projectName.localeCompare(b.projectName);
      if (sort === 'za') return b.projectName.localeCompare(a.projectName);
      return 0;
    });

  // Favorites first when not sorting explicitly
  const sorted =
    sort === 'newest'
      ? [...filtered].sort((a, b) => {
          const aFav = favSet.has(a.id) ? 0 : 1;
          const bFav = favSet.has(b.id) ? 0 : 1;
          if (aFav !== bFav) return aFav - bFav;
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        })
      : filtered;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Project Library</h2>
          <p className="mt-1 text-sm text-slate-500">All impact analyses for your organization.</p>
        </div>
        <Link
          to="/analyses/new"
          className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
        >
          + New analysis
        </Link>
      </div>

      {/* Filter bar */}
      {!loading && !error && analyses.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <input
            type="search"
            placeholder="Search by project name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="min-w-52 flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
          >
            <option value="all">All statuses</option>
            {Object.entries(STATUS_LABEL).map(([val, label]) => (
              <option key={val} value={val}>
                {label}
              </option>
            ))}
          </select>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortOption)}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="az">Name A → Z</option>
            <option value="za">Name Z → A</option>
          </select>
          <label className="flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50">
            <input
              type="checkbox"
              checked={favOnly}
              onChange={(e) => setFavOnly(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-slate-300"
            />
            <span className="text-slate-600">Favorites only</span>
          </label>
          <input
            type="date"
            value={dateAfter}
            onChange={(e) => setDateAfter(e.target.value)}
            title="Created after"
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-500"
          />
          <input
            type="date"
            value={dateBefore}
            onChange={(e) => setDateBefore(e.target.value)}
            title="Created before"
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-500"
          />
        </div>
      )}

      {/* States */}
      {loading && (
        <div className="rounded-2xl bg-white/60 backdrop-blur-xl border border-white/60 shadow-lg shadow-slate-900/5 p-8 text-center">
          <p className="text-sm text-slate-500">Loading…</p>
        </div>
      )}
      {error && (
        <div className="rounded-2xl border border-red-200/80 bg-red-50/80 backdrop-blur-xl shadow-lg shadow-red-900/5 p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
      {!loading && !error && analyses.length === 0 && (
        <div className="rounded-2xl bg-white/60 backdrop-blur-xl border border-white/60 shadow-lg shadow-slate-900/5 p-8 text-center">
          <p className="font-display text-lg bg-gradient-to-br from-slate-900 to-slate-700 bg-clip-text text-transparent">
            No analyses yet.
          </p>
          <p className="mt-2">
            <Link
              to="/analyses/new"
              className="text-sm font-medium text-brand-teal-700 hover:underline"
            >
              Run your first analysis →
            </Link>
          </p>
        </div>
      )}
      {!loading && !error && sorted.length === 0 && analyses.length > 0 && (
        <p className="text-sm text-slate-500">No analyses match your filters.</p>
      )}

      {/* Table — wrapped in a glass card. */}
      {!loading && !error && sorted.length > 0 && (
        <div className="overflow-x-auto rounded-2xl bg-white/60 backdrop-blur-xl border border-white/60 shadow-lg shadow-slate-900/5 p-2">
          <table className="min-w-full border-collapse text-sm">
            <thead>
              <tr className="border-b-2 border-slate-900">
                <th className="w-8 px-3 py-3" aria-label="Favorite" />
                <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">
                  Project
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">
                  Industry
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">
                  Tags
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">
                  Grade
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">
                  Created
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((a) => {
                const isFav = favSet.has(a.id);
                const tags = tagsMap[a.id] ?? [];
                const naics = a.inputs?.step1?.naicsCode;
                const industry = naics
                  ? industryFromNaics(naics)
                  : (a.inputs?.step1?.industryDescription?.slice(0, 20) ?? '—');

                return (
                  <tr
                    key={a.id}
                    className={`border-b border-slate-100 transition hover:bg-slate-50 ${isFav ? 'bg-amber-50/30' : ''}`}
                  >
                    {/* Favorite star */}
                    <td className="px-3 py-3 text-center">
                      <button
                        type="button"
                        onClick={() => void toggleFavorite(a.id)}
                        className={`transition-colors ${isFav ? 'text-amber-400 hover:text-amber-500' : 'text-slate-200 hover:text-amber-300'}`}
                        aria-label={isFav ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        <StarIcon filled={isFav} />
                      </button>
                    </td>

                    {/* Project name */}
                    <td className="px-4 py-3 font-display text-base text-slate-900">
                      <Link to={`/analyses/${a.id}`} className="hover:underline">
                        {a.projectName}
                      </Link>
                    </td>

                    {/* Industry */}
                    <td className="px-4 py-3 text-slate-500">{industry}</td>

                    {/* Tags */}
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1">
                        {tags.map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600"
                          >
                            {tag}
                            <button
                              type="button"
                              onClick={() => void removeTag(a.id, tag)}
                              className="text-slate-400 hover:text-red-500"
                              aria-label={`Remove tag ${tag}`}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                        {addingTagFor === a.id ? (
                          <div className="relative">
                            <input
                              ref={tagInputRef}
                              type="text"
                              value={tagInput}
                              onChange={(e) => setTagInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') void addTag(a.id, tagInput);
                                if (e.key === 'Escape') {
                                  setAddingTagFor(null);
                                  setTagInput('');
                                }
                              }}
                              onBlur={() => {
                                if (!tagInput) {
                                  setAddingTagFor(null);
                                }
                              }}
                              placeholder="tag…"
                              list={`tags-list-${a.id}`}
                              className="w-20 rounded border border-slate-300 px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-slate-500"
                              // biome-ignore lint/a11y/noAutofocus: intentional for inline tag input
                              autoFocus
                            />
                            <datalist id={`tags-list-${a.id}`}>
                              {allOrgTags.map((t) => (
                                <option key={t} value={t} />
                              ))}
                            </datalist>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              setAddingTagFor(a.id);
                              setTagInput('');
                            }}
                            className="rounded-full border border-dashed border-slate-300 px-1.5 py-0.5 text-xs text-slate-400 hover:border-slate-400 hover:text-slate-600"
                          >
                            + tag
                          </button>
                        )}
                      </div>
                    </td>

                    {/* Grade pill */}
                    <td className="px-4 py-3">
                      {a.scoreData ? (
                        <span
                          className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold text-white"
                          style={{ backgroundColor: a.scoreData.gradeColor }}
                          title={`Score: ${a.scoreData.score.toFixed(1)}`}
                        >
                          {a.scoreData.grade}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-300">—</span>
                      )}
                    </td>

                    {/* Status badge */}
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLOR[a.status] ?? 'bg-slate-100 text-slate-600'}`}
                      >
                        {STATUS_LABEL[a.status] ?? a.status}
                      </span>
                    </td>

                    {/* Created date */}
                    <td className="px-4 py-3 text-slate-500">
                      {new Date(a.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-3">
                        {excelEnabled && !!a.results && (
                          <button
                            type="button"
                            onClick={() => void downloadXlsx(a)}
                            className="text-xs font-medium text-emerald-600 hover:text-emerald-800 hover:underline"
                          >
                            ↓ Excel
                          </button>
                        )}
                        <Link
                          to={`/analyses/${a.id}`}
                          className="text-xs font-medium text-slate-500 hover:text-slate-900 hover:underline"
                        >
                          View →
                        </Link>
                        <button
                          type="button"
                          onClick={() => void deleteAnalysis(a)}
                          className="text-xs font-medium text-red-500 hover:text-red-700 hover:underline"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="border-t border-slate-100 px-4 py-2 text-xs text-slate-400">
            {sorted.length} of {analyses.length} analyses
            {favOnly && ` · ${favSet.size} favorite${favSet.size !== 1 ? 's' : ''} total`}
          </p>
        </div>
      )}
    </div>
  );
}
